import { ImagePlus, Loader, X } from 'lucide-react'
import type { ReactNode } from 'react'

import { Button, ProgressRing, formatBytes } from '@krp/ui-kit'

import '../../../shared/platform.css'
import { ActionError } from '../../../shared/ActionError'
import { photoProgressStyle } from '../../../shared/cssVars'
import type { CropBox, PhotoUploadVm } from '../../common-photos/model/types'
import '../../common-photos/ui/commonPhotos.css'
import { PhotoCropReview } from '../../common-photos/ui/PhotoCropReview'

/**
 * Загрузка кадра сканирования в общий каталог (RP-FB56): снимок, кольцо хода по реальным байтам, слова этапа,
 * «Отменить» и, при отказе, «Повторить» с тем же ключом операции. Ошибка — дословно, с кодом, номером запроса и «Скопировать ошибку»;
 * она не исчезает по таймеру. `extra` — дополнительные действия при отказе (например, «Открыть товар без фото»).
 */
export function ScanPhotoRun({
  view,
  onRetry,
  onCancel,
  onClose,
  onAcceptCrop,
  onReload,
  extra,
}: {
  view: PhotoUploadVm
  onRetry: () => void
  onCancel: () => void
  onClose: () => void
  /** «Принять» на проверке обрезки (RP-FB73): сохранить фото по этой рамке. */
  onAcceptCrop: (box: CropBox) => void
  onReload?: (() => void) | undefined
  extra?: ReactNode
}) {
  const percent = view.fraction === undefined ? null : Math.round(view.fraction * 100)
  const style = photoProgressStyle(view.fraction)
  const failed = view.phase === 'failed'
  if (view.review !== undefined) {
    return (
      <div className="platform-crop__host" data-phase={view.phase}>
        <ActionError error={view.error} {...(onReload !== undefined ? { onReload } : {})} />
        <PhotoCropReview review={view.review} busy={view.phase !== 'review'} busyText={view.stage} onAccept={onAcceptCrop} onCancel={onCancel} />
      </div>
    )
  }
  return (
    <div className="platform-photos__run" role="status" aria-live="polite" style={style} data-phase={view.phase}>
      <div className="platform-photos__shot">
        {view.previewUrl === null ? <ImagePlus size={40} aria-hidden="true" /> : <img src={view.previewUrl} alt={`Снимок ${view.fileName}`} />}
        {!failed && (
          <span className="platform-photos__ring">
            <ProgressRing label={view.stage} value={view.fraction} size={72} valueText={view.stage}>
              {percent === null ? <Loader size={22} aria-hidden="true" /> : <span>{percent}%</span>}
            </ProgressRing>
          </span>
        )}
      </div>
      <p className="platform-photos__caption">{view.stage}</p>
      {view.phase === 'sending' && view.total !== null && (
        <p className="platform-photos__meta">
          {formatBytes(view.loaded)} из {formatBytes(view.total)}
        </p>
      )}
      <ActionError error={view.error} {...(onReload !== undefined ? { onReload } : {})} />
      <div className="platform-photos__row">
        {view.cancelable && (
          <Button variant="ghost" icon={<X size={16} aria-hidden="true" />} onClick={onCancel} actionId="platform.scan.photo-cancel">
            Отменить
          </Button>
        )}
        {failed && (
          <>
            <Button variant="primary" onClick={onRetry} actionId="platform.scan.photo-retry">
              Повторить
            </Button>
            <Button onClick={onClose} actionId="platform.scan.photo-close">
              Закрыть
            </Button>
          </>
        )}
        {extra}
      </div>
    </div>
  )
}
